/**
 * ActionContext builder — the ONE place an MCEvent is classified for the
 * action system. Runs once per interaction (right-click / hotkey press);
 * everything downstream dispatches on the typed union instead of re-probing
 * `extendedProps`.
 *
 * Returns null when the event offers no actions (drafts, plain background
 * fills, patient-less generics) — the caller then simply shows no menu.
 */
import { eventToClientTimeSlot } from "../../Calendar/controller/adapters/display-to-client/reverse.adapter";
import { MCEvent } from "../model/types";
import { ActionContext } from "./types";

export const buildActionContext = (event: MCEvent): ActionContext | null => {
  const raw = event.raw;
  const timeSlotType = event.timeSlotType as string;
  const exceptionId = raw.extendedProps?.exceptionId as string | undefined;

  switch (event.kind) {
    case "appointment": {
      // The legacy menu's appointment case requires a patient; without one
      // there is nothing to navigate to or cancel.
      if (event.patientId == null) return null;
      return {
        type: "appointment",
        event,
        raw,
        slot: eventToClientTimeSlot(raw),
        patientId: event.patientId,
        appointmentId: event.appointmentId,
        isContinuous: event.isContinuous,
        isCancelled: event.isCancelled,
      };
    }
    case "group":
      return {
        type: "group",
        event,
        raw,
        isContinuous: event.isContinuous,
        timeSlotId: (raw.extendedProps?.timeSlotId as string | undefined) ?? String(raw.id ?? ""),
        participantNames: event.participantNames ?? [],
      };
    case "break":
      return { type: "break", event, raw };
    case "travel":
      return { type: "travel", event, raw };
    case "absence":
      // CancelledDay is visually an absence but semantically a plan
      // exception: "delete" must restore the plan, not delete a timeslot.
      if (timeSlotType === "CancelledDay") {
        return { type: "cancelledDay", event, raw, exceptionId };
      }
      return { type: "absence", event, raw };
    case "generic": {
      if (timeSlotType === "EventTimeSlot" || timeSlotType === "ContinuousEventTimeSlot") {
        return {
          type: "namedEvent",
          event,
          raw,
          isContinuous: timeSlotType === "ContinuousEventTimeSlot" || event.isContinuous,
          title: event.title,
        };
      }
      if (timeSlotType === "WorkingHours" || timeSlotType === "SimpleWorkingHours") {
        return { type: "workingHours", event, raw, exceptionId };
      }
      return null;
    }
    case "draft":
      return null;
    default:
      return null;
  }
};
